import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { ActivatedRoute } from "@angular/router";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import LocalizationConstants from "src/app/constants/localization-constants";
import RouteNameConstants from "src/app/constants/route-name-constants";
import { ExcelExportType } from "src/app/models/ExcelExport";
import { ToolFilter } from "src/app/models/ToolFilter";
import { CompanyService } from "src/app/services/company.service";
import { GlobalOverlayService } from "src/app/services/global-overlay.service";

import { FsVisitType } from "./../../models/Enums";
import { FsJob } from "./../../models/FsJob";
import { FieldServiceService } from "./../../services/field-service.service";

@Component({
  selector: "app-field-service",
  templateUrl: "./field-service.component.html",
  styleUrls: ["./field-service.component.scss"],
  standalone: false,
})
export class FieldServiceComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  private subscription: Subscription;
  readonly routeNameConstants = RouteNameConstants;
  readonly localizationConstants = LocalizationConstants;

  columns = this.fsService.getColumns();
  columnKeys = this.columns.map((column) => column.key);
  pageSize: number = 10;
  faSpinner = faSpinner;
  // TODO: check what should be included in the filter
  toolFilter: ToolFilter = new ToolFilter();

  fsFilters: FsVisitType[];
  dataSource = new MatTableDataSource<FsJob>([]);
  fsJobsAll: FsJob[] = [];
  ExcelExportType = ExcelExportType;

  constructor(
    private route: ActivatedRoute,
    public companyService: CompanyService,
    private fsService: FieldServiceService,
    private globalOverlayService: GlobalOverlayService,
  ) {}

  ngOnInit() {
    this.route.queryParams.subscribe((params) => {
      const filter = params["filter"];
      if (filter == "last") this.fsFilters = [FsVisitType.Last];
      else if (filter == "planned") this.fsFilters = [FsVisitType.Planned];
      else this.fsFilters = [];

      this.subscription = this.onCompanyChange();
      if (!this.companyService.selectedCompany?.name) return;
      this.getFsList();
    });
  }

  ngAfterViewInit() {
    this.dataSource.filterPredicate = this.quickFilterPredicate;
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  getFsList() {
    this.globalOverlayService.show();
    this.fsService
      .getList(this.companyService.selectedCompany.name)
      .pipe(finalize(() => this.globalOverlayService.close()))
      .subscribe((response) => {
        if (!response) return;
        this.fsJobsAll = response;

        this.dataSource.data = this.fsJobsAll.filter((x) => this.applyVisitTypeFilter(x));
      });
  }

  onCompanyChange() {
    return this.companyService.companyChange.subscribe((company) => {
      if (!company?.name) return;
      this.getFsList();
    });
  }

  toggleLastVisits() {
    const index = this.fsFilters.indexOf(FsVisitType.Last);

    if (index > -1) this.fsFilters.splice(index, 1);
    else this.fsFilters.push(FsVisitType.Last);

    this.dataSource.data = this.fsJobsAll.filter((x) => this.applyVisitTypeFilter(x));
  }

  togglePlannedVisits() {
    const index = this.fsFilters.indexOf(FsVisitType.Planned);

    if (index > -1) this.fsFilters.splice(index, 1);
    else this.fsFilters.push(FsVisitType.Planned);

    this.dataSource.data = this.fsJobsAll.filter((x) => this.applyVisitTypeFilter(x));
  }

  get showLastVisits() {
    return this.fsFilters.length == 1 && this.fsFilters.includes(FsVisitType.Last);
  }

  get showPlannedVisits() {
    return this.fsFilters.length == 1 && this.fsFilters.includes(FsVisitType.Planned);
  }

  get lastVisitsCount() {
    return this.fsJobsAll?.filter((x) => x.status == "Last").length;
  }

  get plannedVisitsCount() {
    return this.fsJobsAll?.filter((x) => x.status == "Planned").length;
  }

  get lastVisitsActive() {
    return this.fsFilters.includes(FsVisitType.Last);
  }

  get plannedVisitsActive() {
    return this.fsFilters.includes(FsVisitType.Planned);
  }

  private applyVisitTypeFilter(fsJob: FsJob): boolean {
    if (!this.fsFilters.length) return true;

    const isLastTypeMatch = fsJob.status === "Last" && this.fsFilters.includes(FsVisitType.Last);
    const isPlannedTypeMatch =
      fsJob.status === "Planned" && this.fsFilters.includes(FsVisitType.Planned);
    return isLastTypeMatch || isPlannedTypeMatch;
  }

  private quickFilterPredicate(fsJob: FsJob, filter: string): boolean {
    if (!filter.trim()) {
      return true;
    }

    const columns = Object.keys(fsJob);
    let found = false;
    filter = filter.toLowerCase();

    columns.forEach((column) => {
      if (found) return;
      if (
        fsJob[column] &&
        typeof fsJob[column] === "string" &&
        fsJob[column].toString().toLowerCase().includes(filter)
      )
        found = true;
    });

    return found;
  }
}
